import { makeStyles } from '@material-ui/core';
import { useState } from 'react';
import { getConfig } from '../../../util/configUtil';
import { useEffect } from 'react';
import { useRef } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { hasOneOfPermission, Permissions, sendUserAudit } from '../../../store/reducers/user';
import { useDispatch, useSelector } from 'react-redux';
import { AUDIT_TYPES } from '../../../util/auditUtil';
import { setSnackbar } from '../../../store/reducers/globals';
import { ctxTreatmentsBoardOpened, setTreatmentsBoardOpened } from '../../../store/reducers/patientContext';

export const useStyles = makeStyles((theme) => ({
    iframe: {
        width: '100vw',
        height: '90vh',
        border: 'none',
        overflowX: 'hidden',
        overflowY: 'hidden',
    },
}));

/**
 * React component that renders an iframe for the Treatments Board.
 * Handles communication between the parent window and the iframe using postMessage.
 * Sends authentication tokens and theme information to the iframe after initialization.
 * Listens for messages from the iframe and logs them to the console.
 *
 * @param {Object} props - Component props.
 * @param {boolean} [props.isAdminMode=false] - Determines whether to show the admin board or the view board.
 * @returns {JSX.Element} The TreatmentsBoard component.
 *
 * @example
 * <TreatmentsBoard isAdminMode={true} />
 */
export const TreatmentsBoard = ({ isAdminMode = false }) => {
    const classes = useStyles();
    const iframeRef = useRef(null);
    const history = useHistory();
    const dispatch = useDispatch();

    const openedTreatmentBoard = useSelector(ctxTreatmentsBoardOpened);

    // Config
    const insightDashboard = getConfig('modules.insightDashboard', {});
    const url = insightDashboard?.url ?? '';
    const token = window.keycloak ? window.keycloak.token : window.auth.token;
    const refreshToken = window.keycloak ? window.keycloak.refreshToken : window.auth.refreshToken;

    const [boardPath, setBoardPath] = useState('');
    const path = isAdminMode ? '/boards' : '/view';

    // Permission
    const canOpenPt = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD, Permissions.AGYLE_CAN_VIEW_ONLY]));

    const [init, setInit] = useState(false);

    useEffect(() => {
        if (openedTreatmentBoard && !init) {
            setBoardPath(openedTreatmentBoard);
            dispatch(setTreatmentsBoardOpened(null));
        }
    }, [openedTreatmentBoard, init]);

    useEffect(() => {
        if (init) {
            const token = window.keycloak ? window.keycloak.token : window.auth.token;
            const refreshToken = window.keycloak ? window.keycloak.refreshToken : window.auth.refreshToken;
            handleSendMessage({ type: 'SET_TOKEN', token, refreshToken });
        }
    }, [init]);

    useEffect(() => {
        if (init) {
            handleSendMessage({ type: 'SET_THEME', theme: window._getTheme() });
        }
    }, [init, window._theme]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== url) return;

            if (event?.data?.type === 'OPEN_PATIENT_CONTEXT' && event?.data?.data) {
                const { data } = event.data;
                const idTB = data.urlPath?.split(path)?.[1];
                if (!canOpenPt) {
                    dispatch(
                        setSnackbar({
                            type: 'warning',
                            message: 'You do not have permission to view this patient record.',
                        }),
                    );
                    return;
                }

                dispatch(sendUserAudit({ patient: data.patient }, AUDIT_TYPES.OPEN_PATIENT_CONTEXT, { fromTreatmentsBoard: true }));

                if (idTB) {
                    dispatch(setTreatmentsBoardOpened(idTB));
                }
                history.push('/patient-context/' + data.patient?._id);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const handleSendMessage = (body) => {
        const wn = document.getElementById('treat-board');
        if (!wn) return;
        const originUrl = new URL(wn.src).origin;

        setTimeout(() => {
            wn.contentWindow.postMessage(body, originUrl);
        }, 500);
    };

    return (
        <>
            <iframe
                id="treat-board"
                ref={iframeRef}
                className={classes.iframe}
                src={`${url}${path}${boardPath}?token=${encodeURIComponent(token)}&refreshToken=${encodeURIComponent(refreshToken)}`}
                title="Treatments Board"
                onLoad={() => setInit(true)}
                onError={(e) => console.error('iFrame Error', e)}
            />
        </>
    );
};
